import { Base } from "../core/base.model";

/**
 * Modelos del modulo de asistencia.
 *
 * Espejo de los beans de CosCore. Observese lo que NO viaja en ninguno
 * de ellos: la empresa. El servidor la resuelve desde la sesion y pisa
 * cualquier valor que llegue del cliente, asi que mandarla desde aqui
 * seria ruido en el mejor de los casos.
 */

/** OFICINA | HOME_OFFICE | MIXTO */
export type Modalidad = "OFICINA" | "HOME_OFFICE" | "MIXTO";

/** OFICINA_PROPIA | SITIO_CLIENTE | HOME_OFFICE */
export type TipoUbicacion = "OFICINA_PROPIA" | "SITIO_CLIENTE" | "HOME_OFFICE";

/** KIOSCO | APP | AMBOS */
export type CanalRegistro = "KIOSCO" | "APP" | "AMBOS";

export type EstatusAsistencia =
  | "PUNTUAL"
  | "RETARDO"
  | "FALTA"
  | "SIN_HORARIO"
  | "DESCANSO"
  | "VACACIONES"
  | "PERMISO"
  | "JUSTIFICADO"
  | "PENDIENTE";

/**
 * Un sitio de trabajo.
 *
 * canalRegistro es la decision que pidio el negocio poder tomar sitio
 * por sitio: en la mayoria de los sitios de cliente no habra kiosco.
 */
export class Ubicacion extends Base {
  id: number;
  nombre: string;
  tipo: TipoUbicacion;
  idCliente: number;
  nombreCliente: string;
  estatus: string;
  zonaHoraria: string;
  canalRegistro: CanalRegistro;
  exigeGeocerca: boolean;
  latitud: number;
  longitud: number;
  radioMetros: number;
  mensaje: string;
}

/** Un dia del patron semanal; solo aplica a MIXTO. */
export class ModalidadDia extends Base {
  idModalidadDia: number;
  idModalidad: number;
  /** 1 = lunes ... 7 = domingo. */
  diaSemana: number;
  modalidadAm: Modalidad;
  modalidadPm: Modalidad;
}

/** Modalidad base del empleado, con vigencia. */
export class EmpleadoModalidad extends Base {
  idModalidad: number;
  idEmpleado: number;
  modalidad: Modalidad;
  idUbicacionBase: number;
  zonaHoraria: string;
  fechaInicioVigencia: string | Date;
  fechaFinVigencia: string | Date;
  estatus: string;
  dias: ModalidadDia[] = [];
  mensaje: string;
}

/**
 * Un renglon de la pantalla de modalidades: una persona y donde se le
 * espera hoy.
 *
 * La base son los EMPLEADOS, no las modalidades: la pregunta que resuelve
 * esa pantalla es a quien le FALTA, y quien no tiene modalidad no tiene
 * fila que listar. Por eso todo lo de la modalidad puede venir nulo.
 */
export class ModalidadRenglon extends Base {
  idEmpleado: number;
  usuario: string;
  nombreEmpleado: string;
  idModalidad: number;
  modalidad: Modalidad;
  idUbicacionBase: number;
  nombreUbicacion: string;
  zonaHoraria: string;
  fechaInicioVigencia: string | Date;
  fechaFinVigencia: string | Date;
}

/** La excepcion de un dia concreto: "el martes me quedo en casa". */
export class PlanDia extends Base {
  idPlan: number;
  idEmpleado: number;
  fecha: string | Date;
  modalidadAm: Modalidad;
  /** null = la tarde es igual que la manana. */
  modalidadPm: Modalidad;
  idUbicacion: number;
  /** EMPLEADO | LIDER | RH */
  origenPlan: string;
  comentario: string;
}

/** El veredicto de un dia, tal como lo ve el propio empleado. */
export class AsistenciaDia extends Base {
  idAsistencia: number;
  idEmpleado: number;
  fecha: string | Date;
  horaEsperadaEntrada: string;
  horaEsperadaSalida: string;
  minutosTolerancia: number;
  modalidadAm: Modalidad;
  modalidadPm: Modalidad;
  idUbicacionEsperada: number;
  zonaHoraria: string;
  horaEntradaReal: string | Date;
  horaSalidaReal: string | Date;
  origenEntrada: string;
  ubicacionDistinta: boolean;
  minutosRetardo: number;
  minutosSalidaAnticipada: number;
  minutosTrabajados: number;
  estatus: EstatusAsistencia;
  mensaje: string;
  /**
   * false cuando la empresa no muestra el veredicto al empleado: estatus,
   * mensaje y minutos de retardo llegan vacios a proposito y NO hay que
   * pintarlos (un estatus vacio no es "En curso"). Ausente = true, que es
   * lo que respondia el servidor antes de existir el parametro.
   */
  veredictoVisible?: boolean;
}

/**
 * Lo que se puede pedir en el monitoreo.
 *
 * Sirve tambien de rango para "mi plan": los dos son desde/hasta con un
 * empleado opcional, y duplicar el tipo solo llevaria a que uno de los
 * dos se quedara atras.
 */
export class MonitoreoFiltro extends Base {
  desde: string;
  hasta: string;
  idCliente: number;
  idUbicacion: number;
  estatus: string;
  idEmpleado: number;
  modalidad: string;
  /** Texto libre contra nombre y usuario; lo usa la pantalla de modalidades. */
  busqueda: string;
  /** Solo quien NO tiene modalidad vigente. */
  soloSinAsignar: boolean;
  rowInicio: number;
  regPagina: number;
}

/** Un renglon del monitoreo y del reporte por cliente. */
export class MonitoreoRenglon extends Base {
  idEmpleado: number;
  usuario: string;
  nombreEmpleado: string;
  fecha: string;
  idUbicacionEsperada: number;
  nombreUbicacion: string;
  idCliente: number;
  nombreCliente: string;
  modalidadAm: Modalidad;
  modalidadPm: Modalidad;
  horaEsperadaEntrada: string;
  horaEntradaReal: string;
  horaSalidaReal: string;
  origenEntrada: string;
  ubicacionDistinta: boolean;
  minutosRetardo: number;
  minutosTrabajados: number;
  estatus: EstatusAsistencia;
}

/**
 * Una estacion de kiosco.
 *
 * tokenEnClaro solo viene poblado en la respuesta de la emision: es la
 * unica vez que existe, porque en base solo queda su hash.
 */
export class Estacion extends Base {
  idEstacion: number;
  idUbicacion: number;
  nombre: string;
  zonaHoraria: string;
  estatus: string;
  tokenEnClaro: string;
  ultimaSincronizacion: string | Date;
}

/** Un telefono vinculado para el registro remoto. */
export class Dispositivo extends Base {
  idDispositivo: number;
  idEmpleado: number;
  /** Solo de lectura: lo resuelve la bandeja de aprobacion por JOIN. */
  nombreEmpleado: string;
  usuario: string;
  huellaDispositivo: string;
  plataforma: string;
  modelo: string;
  estatus: string;
  fechaSolicitud: string | Date;
  ultimaVerificacion: string | Date;
}

/** Parametros de asistencia de la empresa. */
export class AsistenciaConfig extends Base {
  /** Mensaje de negocio; el backend lo usa para explicar un rechazo. */
  mensaje: string;
  idConfig: number;
  minutosTolerancia: number;
  reglaFalta: string;
  minutosUmbralFalta: number;
  retardosPorPeriodo: number;
  periodoAcumulacion: string;
  modoMixtoDia: string;
  horaCorteFalta: string;
  mostrarVeredictoEmpleado: boolean;
  minutosMaxDesfaseOffline: number;
  nivelVerificacionRemota: string;
  cambioDispositivoRequiereAprobacion: boolean;
  dispositivosPorEmpleado: number;
  zonaHorariaDefault: string;
  fechaInicioEvaluacion: string | Date;
  estatus: string;
}
